// Implements: F-UIX-019, F-GEN-036, F-UIX-007
/**
 * The catalogue picker of a Modelos tier (mockup v3 P7, v3 `openPicker`): a search field that is a
 * combobox over a listbox of the provider's models (name, the «razonamiento» badge, provider and
 * id, context, maximum output, USD per million tokens in and out), the «Solo con razonamiento»
 * switch, and a foot that says how many models the catalogue holds and when it was read.
 *
 * The focus never leaves the search field: ArrowDown/ArrowUp move the active option with wrap
 * (`aria-activedescendant`, `aria-selected`), Enter picks it, a press picks any. The active option
 * goes back to the first whenever the list changes, and the field says it is collapsed while no
 * option is listed. The search is bounded as the server bounds it (100 characters). Pure presentation apart from that active
 * option: the container searches, debounces and picks.
 */

import { useId, useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Switch } from "../../../shared/ui/atoms/Switch";
import { dayText } from "../../../shared/ui/dayText";
import type { CatalogueModelView, CatalogueView } from "../../../types/modelCatalogue";
import { catalogueDollars, tokenText } from "../model/catalogueFill";
import styles from "./ModelCataloguePicker.module.css";

const NO_VALUE = "—";
const NO_ITEMS: readonly CatalogueModelView[] = [];
/** The longest search the server takes (C7.2: `q` 0..100 characters). */
const SEARCH_MAX_LENGTH = 100;

export type CatalogueState =
  | { readonly kind: "loading" }
  | { readonly kind: "unavailable" }
  | { readonly kind: "ready"; readonly view: CatalogueView };

export interface ModelCataloguePickerProps {
  readonly query: string;
  readonly onQuery: (query: string) => void;
  readonly reasoningOnly: boolean;
  readonly onReasoningOnly: (on: boolean) => void;
  readonly state: CatalogueState;
  /** The model the tier uses now, marked «seleccionado» in the list. */
  readonly selectedModelId: string;
  readonly onPick: (model: CatalogueModelView) => void;
}

export function ModelCataloguePicker({
  query,
  onQuery,
  reasoningOnly,
  onReasoningOnly,
  state,
  selectedModelId,
  onPick,
}: ModelCataloguePickerProps): ReactElement {
  const { t } = useTranslation();
  const ids = useId();
  const listId = `${ids}-list`;
  const items = state.kind === "ready" ? state.view.items : NO_ITEMS;
  // The active option belongs to the list it was moved in: a new list starts again at the first.
  const [active, setActive] = useState<{ readonly list: readonly CatalogueModelView[]; readonly index: number }>({
    list: items,
    index: 0,
  });
  const index = active.list === items ? active.index : 0;

  const optionId = (position: number): string => `${ids}-option-${String(position)}`;
  const current = items.length === 0 ? undefined : Math.min(index, items.length - 1);

  function move(step: 1 | -1): void {
    if (items.length === 0) return;
    setActive({ list: items, index: (index + step + items.length) % items.length });
  }

  return (
    <div className={styles.picker}>
      <div className={styles.head}>
        <input
          className={styles.search}
          type="search"
          role="combobox"
          aria-label={t("models.picker.search")}
          placeholder={t("models.picker.search")}
          aria-expanded={items.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={current === undefined ? undefined : optionId(current)}
          autoComplete="off"
          maxLength={SEARCH_MAX_LENGTH}
          data-autofocus
          value={query}
          onChange={(event) => {
            onQuery(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault();
              move(event.key === "ArrowDown" ? 1 : -1);
            } else if (event.key === "Enter") {
              event.preventDefault();
              const model = current === undefined ? undefined : items[current];
              if (model !== undefined) onPick(model);
            }
          }}
        />
        <Switch label={t("models.picker.reasoningOnly")} checked={reasoningOnly} onChange={onReasoningOnly} />
      </div>
      <div className={styles.columns} aria-hidden="true">
        <span>{t("models.picker.colModel")}</span>
        <span>{t("models.picker.colContext")}</span>
        <span>{t("models.picker.colOutput")}</span>
        <span>{t("models.picker.colPrice")}</span>
      </div>
      <ul id={listId} role="listbox" aria-label={t("models.picker.list")} className={styles.list}>
        {items.map((model, index) => (
          <li
            key={model.model_id}
            id={optionId(index)}
            role="option"
            aria-selected={index === current}
            className={styles.option}
            onMouseDown={(event) => {
              // The search field keeps the focus (the shared Combobox's rule).
              event.preventDefault();
              onPick(model);
            }}
          >
            <span className={styles.main}>
              <span className={styles.name}>
                {model.name}
                {model.supports_reasoning ? <span className={styles.badge}>{t("models.picker.reasoningBadge")}</span> : null}
              </span>
              <span className={styles.meta}>
                {t("models.picker.optionMeta", { provider: model.provider, model: model.model_id })}
                {model.model_id === selectedModelId ? ` · ${t("models.picker.selected")}` : null}
              </span>
            </span>
            <span className={styles.figure}>{tokenText(model.context_length)}</span>
            <span className={styles.figure}>{tokenText(model.max_output_tokens)}</span>
            <span className={styles.figure}>
              {model.price === null
                ? NO_VALUE
                : t("models.picker.optionPrice", {
                    prompt: catalogueDollars(model.price.prompt_micros_per_million_tokens),
                    completion: catalogueDollars(model.price.completion_micros_per_million_tokens),
                  })}
            </span>
          </li>
        ))}
      </ul>
      {state.kind === "ready" && items.length === 0 ? <p className={styles.empty}>{t("models.picker.empty")}</p> : null}
      {state.kind === "unavailable" ? (
        <p className={styles.unavailable} role="alert">
          {t("models.picker.unavailable")}
        </p>
      ) : null}
      {state.kind === "ready" ? (
        <p className={styles.foot}>
          {t("models.picker.foot", { count: state.view.total, updated: dayText(state.view.fetched_at) })}
        </p>
      ) : null}
    </div>
  );
}
